import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { AlertTriangle, Clock, Mail, RefreshCw } from "lucide-react";
import { getFormExpiry, sendFormExpiryReminder } from "../../lib/api";

function StatusPill({ days }) {
  if (days < 0) {
    return <span className="text-xs px-2 py-0.5 rounded-full border bg-red-100 text-red-700 border-red-200">Expired {Math.abs(days)}d ago</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded-full border bg-amber-100 text-amber-700 border-amber-200">In {days}d</span>;
}

function StudentTable({ entries, navigate, testid }) {
  if (!entries.length) {
    return <p className="text-sm text-muted-foreground py-2" data-testid={`${testid}-empty`}>None</p>;
  }
  return (
    <table className="w-full text-sm" data-testid={testid}>
      <thead>
        <tr className="text-left text-muted-foreground border-b">
          <th className="py-1.5 pr-3">Student</th>
          <th className="py-1.5 pr-3">#</th>
          <th className="py-1.5 pr-3">Expiry</th>
          <th className="py-1.5 pr-3">Status</th>
          <th className="py-1.5"></th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id} className="border-b last:border-0" data-testid={`${testid}-row-${e.id}`}>
            <td className="py-1.5 pr-3 font-medium">{e.name}</td>
            <td className="py-1.5 pr-3">{e.student_number || "—"}</td>
            <td className="py-1.5 pr-3">{e.expiry}</td>
            <td className="py-1.5 pr-3"><StatusPill days={e.days_until} /></td>
            <td className="py-1.5 text-right">
              <button
                type="button"
                className="text-xs text-blue-600 underline"
                onClick={() => navigate(`/students/${e.id}`)}
                data-testid={`${testid}-open-${e.id}`}
              >
                Open file
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function FormExpirationsTab() {
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFormExpiry(30);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load form expirations");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await sendFormExpiryReminder();
      const d = res.data || {};
      if (d.skipped === "nothing_to_report") toast.info("Nothing to report — no reminder sent.");
      else if (d.skipped) toast.warning(`Not sent: ${d.skipped}`);
      else toast.success(`Reminder emailed to ${d.sent} admin${d.sent === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSending(false);
    }
  };

  const total = data ? data.forms.reduce((n, f) => n + f.expired.length + f.expiring_soon.length, 0) : 0;

  return (
    <div className="space-y-6" data-testid="form-expirations-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Form Expirations</h2>
          <p className="text-sm text-muted-foreground">
            OTC Medical Authorization &amp; Photo Release consents that are expired or expiring within 30 days.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="form-expiry-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={handleSend} disabled={sending} data-testid="form-expiry-send-reminder">
            <Mail className="w-4 h-4 mr-1.5" /> {sending ? "Sending…" : "Email admins now"}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data ? null : total === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground" data-testid="form-expiry-all-clear">
          All consent forms are current — nothing expired or expiring within 30 days.
        </CardContent></Card>
      ) : (
        data.forms.map((f) => (
          <Card key={f.key} data-testid={`form-expiry-card-${f.key}`}>
            <CardHeader>
              <CardTitle className="text-base">{f.label}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700 mb-1">
                  <AlertTriangle className="w-4 h-4" /> Expired ({f.expired.length})
                </p>
                <StudentTable entries={f.expired} navigate={navigate} testid={`expired-${f.key}`} />
              </div>
              <div>
                <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 mb-1">
                  <Clock className="w-4 h-4" /> Expiring within 30 days ({f.expiring_soon.length})
                </p>
                <StudentTable entries={f.expiring_soon} navigate={navigate} testid={`expiring-${f.key}`} />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
